import LabelInput from "../labelInput";
import MonthYearField from "../monthYearField";
import RadioGroup from "../RadioGroup";
import { useState, useEffect } from "react";
import { fetchVehicleBodyType } from "../../services/simpleListItemsService";
import FieldStack from "../FieldStack";
import Button from "../Button";

const VehicleLookupQuestions = ({
  STATE,
  register,
  trigger,
  errors,
  watch,
  setValue,
  control,
  isLoading,
  dropDownLoading,
  modifications,
  vehicleBodyTypes,
  security,
  handleYesClick,
  handleEditClick,
}) => {
  const convertArrayFormat = (array) => {
    return Array.isArray(array)
      ? array.map((item) => ({
          value: item.listItemId,
          label: item.listItemDescription,
        }))
      : [];
  };

  const vehicleDetails = [
    `Is ${watch("vehImported") === "Yes" ? "imported" : "NOT imported"}`,
    "Is NOT Q plated",
    "Is right hand drive",
    `has ${watch("seats") ? `${watch("seats")} seats` : "no seats found"}`,
    "Is NOT modified in any way",
  ];

  return (
    <FieldStack className="tw-w-full">
      {STATE.vehicleConfirmationBox && (
        <div className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-dis-border-strong tw-p-4 sm:tw-p-5">
          <p className="tw-m-0 tw-text-base tw-font-bold tw-text-dis-navy">
            This Vehicle:
          </p>
          <ul className="tw-mb-0 tw-mt-3 tw-grid tw-list-none tw-grid-cols-1 tw-gap-x-6 tw-gap-y-2 tw-p-0 sm:tw-grid-cols-2">
            {vehicleDetails.map((detail) => (
              <li
                key={detail}
                className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-leading-[1.5] tw-text-dis-text-2"
              >
                <span
                  className="tw-h-4 tw-w-[3px] tw-shrink-0 tw-rounded-full tw-bg-dis-sky"
                  aria-hidden="true"
                />
                {detail}
              </li>
            ))}
          </ul>
          <div className="tw-mt-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-dis-divider tw-pt-4">
            <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-dis-navy">
              Are these details correct?
            </p>
            <div className="tw-mt-3 tw-flex tw-justify-start tw-gap-2.5">
              <Button
                onClick={handleYesClick}
                type="button"
                variant="main"
                size="small"
              >
                Yes
              </Button>

              <Button
                onClick={handleEditClick}
                type="button"
                variant="soft"
                size="small"
              >
                No
              </Button>
            </div>
          </div>

          {/* Invisible field for storing the user's choice */}
          <input
            type="hidden"
            {...register("vehicleDetailsCorrect", {
              validate: (value) =>
                value === "Yes" || value === "No" || "Please select Yes or No",
            })}
          />
        </div>
      )}

      {(STATE.showBasicLookupQuestions || STATE.showExtraLookupQuestions) && (
        <div className="tw-border-t tw-border-dis-divider" />
      )}

      {STATE.showBasicLookupQuestions && (
        <>
          <RadioGroup
            name="vehImported"
            mainLabel="Is your vehicle imported?"
            register={register}
            trigger={trigger}
            errors={errors.vehImported}
          />
          <RadioGroup
            name="vehicleQPlated"
            mainLabel="Is your vehicle Q-plated?"
            register={register}
            trigger={trigger}
            errors={errors.vehicleQPlated}
          />

          <RadioGroup
            name="rightHandDrive"
            mainLabel="Is your vehicle a right hand drive?"
            register={register}
            trigger={trigger}
            errors={errors.rightHandDrive}
          />

          <LabelInput
            id="seats"
            label="Number of seats, including the driver?"
            type="number"
            placeholder="e.g. 5"
            maxLength={1}
            onInput={(e) => {
              const value = e.target.value;
              if (!/^[1-9]$/.test(value)) {
                e.target.value = value.slice(0, -1);
              }
            }}
            register={register("seats", {
              required: "Required",
              pattern: {
                value: /^[1-9]$/,
                message: "Please enter a single digit between 1 and 9",
              },
              min: {
                value: 1,
                message: "Please enter a number between 1 and 9",
              },
              max: {
                value: 9,
                message: "Please enter a number between 1 and 9",
              },
            })}
            errors={errors.seats}
            watch={watch("seats")}
            trigger={trigger}
          />

          {(!watch("bodyTypeId") || STATE.manualBodyTypeId) && (
            <LabelInput
              id="manualBodyTypeId"
              label="Vehicle body type"
              type="select"
              options={convertArrayFormat(vehicleBodyTypes)}
              control={control}
              placeholder="Select body type"
              register={register("manualBodyTypeId", {
                required: "Required",
              })}
              errors={errors.manualBodyTypeId}
              watch={watch("manualBodyTypeId")}
              disabled={dropDownLoading}
              isLoading={dropDownLoading}
              trigger={trigger}
              isSearchable={true}
            />
          )}

          <RadioGroup
            name="vehicleModified"
            mainLabel="Has your vehicle been modified in any way?"
            register={register}
            trigger={trigger}
            errors={errors.vehicleModified}
          />
          {watch("vehicleModified") === "Yes" && (
            <LabelInput
              id="modifications"
              label="Modifications"
              type="select"
              placeholder="Select Modifications"
              options={convertArrayFormat(modifications)}
              register={register("modifications", {
                required: "Required",
              })}
              errors={errors?.modifications}
              watch={watch("modifications")}
              control={control}
              disabled={dropDownLoading}
              isLoading={dropDownLoading}
              trigger={trigger}
            />
          )}
        </>
      )}
      {STATE.showExtraLookupQuestions && (
        <>
          <RadioGroup
            name="dashCam"
            mainLabel="Do you have a working Dashcam installed in your vehicle?"
            register={register}
            trigger={trigger}
            errors={errors.dashCam}
          />

          <RadioGroup
            name="security"
            mainLabel="Does your vehicle have a security device fitted?"
            register={register}
            trigger={trigger}
            errors={errors.security}
          />

          {watch("security") === "Yes" && (
            <LabelInput
              id="securityDevice"
              label="What type of security device you have fitted"
              type="select"
              control={control}
              isSearchable={true}
              options={convertArrayFormat(security)}
              placeholder="Enter security device "
              register={register("securityDevice", {
                required: "Required",
              })}
              disabled={dropDownLoading}
              isLoading={dropDownLoading}
              errors={errors.securityDevice}
              watch={watch("securityDevice")}
              trigger={trigger}
            />
          )}
          <RadioGroup
            name="vehiclePurchased"
            mainLabel="Have you purchased the vehicle?"
            register={register}
            trigger={trigger}
            errors={errors.vehiclePurchased}
            onNo={() => {
              setValue("purchaseDateMonth", null);
              setValue("purchaseDateYear", null);
              setValue("ownedPrevVehicle", null);
              setValue("vehRegistration", null);
            }}
          />
          {watch("vehiclePurchased") === "Yes" && (
            <>
              <MonthYearField
                label="What date did you purchase the vehicle?"
                control={control}
                monthID="purchaseDateMonth"
                yearID="purchaseDateYear"
                errors={{
                  purchaseDateMonth: errors.purchaseDateMonth,
                  purchaseDateYear: errors.purchaseDateYear,
                }}
                trigger={trigger}
                watch={watch}
                register={register}
              />
            </>
          )}

          {watch("datedifference") &&
            watch("vehiclePurchased") === "Yes" &&
            (watch("purchaseDateMonth") && watch("purchaseDateYear")) !==
              null && (
              <RadioGroup
                name="vehRegistration"
                mainLabel="Do you have the registration of your previous vehicle?"
                register={register}
                trigger={trigger}
                errors={errors.vehRegistration}
                onNo={() => {
                  setValue("ownedPrevVehicle", null);
                }}
              />
            )}

          {watch("vehRegistration") === "Yes" && (
            <RadioGroup
              name="ownedPrevVehicle"
              mainLabel="Did you own the previous vehicle for more than 12 months?"
              register={register}
              trigger={trigger}
              errors={errors.ownedPrevVehicle}
            />
          )}
        </>
      )}
    </FieldStack>
  );
};

export default VehicleLookupQuestions;
